import { updateDevices, bundleBusy } from '@theme/nailnova-cart-bundles';
import { PACK_SIZES } from '@theme/nailnova-bundle-plan';

class NailNovaCartUpgrade extends HTMLElement {
  connectedCallback() {
    this.enhance();
    this.handleClick = event => { if (event.target.closest('[data-upgrade-button]')) this.upgrade(); };
    this.addEventListener('click', this.handleClick);
    // Horizon can retain this custom element while morphing its children.
    this.observer = new MutationObserver(() => this.enhance());
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
  }

  disconnectedCallback() { this.observer?.disconnect(); this.removeEventListener('click', this.handleClick); }

  enhance() {
    this.button = this.querySelector('[data-upgrade-button]');
    if (this.button?.hidden) this.button.hidden = false;
    const fallback = this.querySelector('.nn-cart-upgrade__fallback');
    if (fallback && !fallback.hidden) fallback.hidden = true;
  }

  async upgrade() {
    if (bundleBusy()) return;
    const previous = PACK_SIZES.get(Number(this.dataset.sourceVariant));
    const quantity = PACK_SIZES.get(Number(this.dataset.targetVariant));
    try { await updateDevices({ key: this.dataset.sourceKey, previous, quantity, targetVariant: Number(this.dataset.targetVariant) }, this); }
    catch {
      const error = this.querySelector('.nn-cart-upgrade__error');
      if (error && this.isConnected) { error.textContent = this.dataset.error; error.hidden = false; }
    }
  }
}

const demoTimerKey = 'nailnova-bundle-preview-expiry-v2';
let demoTimerDeadline;

// Keep the presentation clock in sync across the header, product and both carts.
// The saved deadline survives navigation/publication; expired demos start a new cycle.
function demoDeadline(now, duration) {
  let stored;
  try { stored = Number(localStorage.getItem(demoTimerKey)); } catch { /* Shared memory covers unavailable storage. */ }
  let deadline = stored;
  if (!Number.isFinite(deadline) || deadline <= 0 || deadline - now > duration) deadline = demoTimerDeadline;
  if (!Number.isFinite(deadline) || deadline <= 0 || deadline - now > duration || deadline <= now) deadline = now + duration;
  demoTimerDeadline = deadline;
  if (stored !== deadline) {
    try { localStorage.setItem(demoTimerKey, String(deadline)); } catch { /* The current page still shares one clock. */ }
  }
  return deadline;
}

class NailNovaCartCountdown extends HTMLElement {
  connectedCallback() {
    clearInterval(this.interval);
    this.observer?.disconnect();
    if (this.storageListener) window.removeEventListener('storage', this.storageListener);
    this.deadline = Date.parse(this.dataset.deadline);
    this.cycleDuration = Number(this.dataset.cycleSeconds) * 1000;
    if (Number.isFinite(this.cycleDuration) && this.cycleDuration > 0) {
      this.deadline = demoDeadline(Date.now(), this.cycleDuration);
    } else if (this.dataset.previewSeconds) {
      const key = 'nailnova-bundle-preview-expiry-v2';
      try {
        const saved = localStorage.getItem(key);
        this.deadline = saved ? Number(saved) : Date.now() + Number(this.dataset.previewSeconds) * 1000;
        if (!saved) localStorage.setItem(key, String(this.deadline));
      } catch { this.deadline = Date.now() + Number(this.dataset.previewSeconds) * 1000; }
    }
    if (!Number.isFinite(this.deadline) || this.deadline <= Date.now()) return;
    this.output = this.querySelector('[data-countdown-output]');
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
    this.observer = new MutationObserver(() => this.tick());
    this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.storageListener = event => { if (event.key === demoTimerKey || event.key === null) this.tick(); };
    window.addEventListener('storage', this.storageListener);
  }
  disconnectedCallback() { clearInterval(this.interval); this.observer?.disconnect(); if (this.storageListener) window.removeEventListener('storage', this.storageListener); }
  tick() {
    const now = Date.now();
    if (Number.isFinite(this.cycleDuration) && this.cycleDuration > 0) this.deadline = demoDeadline(now, this.cycleDuration);
    if (!Number.isFinite(this.deadline) || this.deadline <= now) { if (!this.hidden) this.hidden = true; clearInterval(this.interval); return; }
    const remaining = Math.ceil((this.deadline - now) / 1000);
    const days = Math.floor(remaining / 86400);
    const hours = Math.floor(remaining % 86400 / 3600);
    const minutes = Math.floor(remaining % 3600 / 60);
    const seconds = remaining % 60;
    this.output = this.querySelector('[data-countdown-output]');
    if (!this.output) return;
    const label = `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    this.output.setAttribute('aria-label', `${this.querySelector('.nn-countdown__title')?.textContent.trim() || ''} ${label}`);
    const values = { days: String(days), hours: String(hours).padStart(2, '0'), minutes: String(minutes).padStart(2, '0'), seconds: String(seconds).padStart(2, '0') };
    this.output.querySelectorAll('[data-countdown-unit]').forEach(cell => {
      const value = values[cell.dataset.countdownUnit];
      if (cell.textContent !== value) cell.textContent = value;
    });
    if (this.hidden) this.hidden = false;
  }
}

if (!customElements.get('nailnova-cart-upgrade')) customElements.define('nailnova-cart-upgrade', NailNovaCartUpgrade);
if (!customElements.get('nailnova-cart-countdown')) customElements.define('nailnova-cart-countdown', NailNovaCartCountdown);

// Delegation also covers review panels replaced by cart section updates.
document.addEventListener('click', event => {
  const closeButton = event.target.closest?.('[data-cart-review-close]');
  if (!closeButton) return;
  const review = closeButton.closest('.nn-cart-review');
  if (!review) return;
  review.open = false;
  review.querySelector('summary')?.focus();
});
